// utils/formGuard.js — keeps typed work in pop-up windows from being lost.  [marker: no-lost-work v1] [marker: confirm-polish v1]
//  • The — button in a pop-up's title bar shrinks it to a tab at the bottom of the screen; click the tab to carry on.
//  • Clicking outside a pop-up you've typed in no longer closes it.
//  • Cancel / ✕ / Close on a pop-up with changes asks "Discard your changes?" first.
//  • Refreshing or closing the tab with unsaved changes asks the browser's "Leave site?" question.
//  • What you type is kept as a draft in this browser. Reopen the same pop-up and it offers to restore it.
// Works on every pop-up that uses the app's standard .modal-overlay / .modal-backdrop wrapper, with no per-page code.
import toast from 'react-hot-toast';

const OVERLAY = '.modal-overlay, .modal-backdrop';
const FIELDS = 'input, textarea, select, [contenteditable="true"]';
const PREFIX = 'njjba-draft:';
const MAX_AGE_MS = 14 * 24 * 3600 * 1000;
const state = new WeakMap(); // overlay element -> { dirty, key, saveTimer, submitting, offered }

const ls = {
  get(k) { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch (_) { return null; } },
  set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (_) { /* storage full or blocked */ } },
  del(k) { try { localStorage.removeItem(k); } catch (_) { /* ignore */ } },
};

function st(ov) { let s = state.get(ov); if (!s) { s = { dirty: false }; state.set(ov, s); } return s; }
const PUBLIC = /^\/(embed|join|portal|kiosk|jobs|login|forgot-password|reset-password)(\/|$)/; // members' and public pages: no drafts kept
function overlayOf(el) { return !PUBLIC.test(window.location.pathname) && el && el.closest ? el.closest(OVERLAY) : null; }
function topOverlay() { const all = document.querySelectorAll(OVERLAY); return all.length ? all[all.length - 1] : null; }

function titleOf(ov) {
  const t = ov.querySelector('.modal-title, .modal-header h1, .modal-header h2, .modal-header h3, h2, h3');
  return (t ? t.textContent : '').replace(/\s+/g, ' ').trim().slice(0, 80);
}
function keyOf(ov) { const t = titleOf(ov); return t ? PREFIX + window.location.pathname + '|' + t : null; }

function skip(el) {
  if (el.closest('[data-no-draft]')) return true;
  if (el.isContentEditable) return false;
  const type = (el.type || '').toLowerCase();
  return ['password', 'file', 'hidden', 'submit', 'button', 'reset', 'image'].includes(type) || el.disabled;
}
function fieldId(el, i) {
  const lab = el.id && document.querySelector(`label[for="${CSS.escape(el.id)}"]`);
  const near = el.closest('.form-group, label');
  const label = (lab && lab.textContent) || (near && near.querySelector('label, .form-label') && near.querySelector('label, .form-label').textContent) || '';
  return [el.tagName, el.type || '', el.name || '', el.id || '', el.getAttribute('placeholder') || '', el.getAttribute('aria-label') || '', label.trim().slice(0, 40), i].join('|');
}
function fields(ov) { return Array.from(ov.querySelectorAll(FIELDS)).filter(el => !skip(el)); }
function read(el) {
  if (el.isContentEditable) return el.innerHTML;
  if (el.type === 'checkbox' || el.type === 'radio') return !!el.checked;
  if (el.tagName === 'SELECT' && el.multiple) return Array.from(el.selectedOptions).map(o => o.value);
  return el.value;
}
function snapshot(ov) { const out = {}; fields(ov).forEach((el, i) => { out[fieldId(el, i)] = read(el); }); return out; }

const setters = {
  INPUT: Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set,
  TEXTAREA: Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value').set,
  SELECT: Object.getOwnPropertyDescriptor(window.HTMLSelectElement.prototype, 'value').set,
};
function write(el, v) {
  if (el.isContentEditable) { if (el.innerHTML !== v) { el.innerHTML = v; el.dispatchEvent(new Event('input', { bubbles: true })); } return; }
  if (el.type === 'checkbox' || el.type === 'radio') { if (!!el.checked !== !!v) el.click(); return; }
  if (el.tagName === 'SELECT' && el.multiple) return; // not restored
  if (el.value === v) return;
  setters[el.tagName].call(el, v);
  el.dispatchEvent(new Event(el.tagName === 'SELECT' ? 'change' : 'input', { bubbles: true }));
  if (el.tagName !== 'SELECT') el.dispatchEvent(new Event('change', { bubbles: true }));
}
function restore(ov, values) {
  // two passes: some pickers fill in their choices only after another field is set
  const pass = () => fields(ov).forEach((el, i) => { const k = fieldId(el, i); if (k in values) { try { write(el, values[k]); } catch (_) { /* ignore */ } } });
  pass(); setTimeout(pass, 250); setTimeout(pass, 800);
}

function saveSoon(ov) {
  const s = st(ov); if (!s.key) s.key = keyOf(ov); if (!s.key) return;
  clearTimeout(s.saveTimer);
  s.saveTimer = setTimeout(() => { if (document.contains(ov)) ls.set(s.key, { at: Date.now(), values: snapshot(ov) }); }, 400);
}

function offerDraft(ov) {
  const s = st(ov); if (s.offered) return; s.offered = true;
  s.key = keyOf(ov); if (!s.key) return;
  const d = ls.get(s.key);
  if (!d || !d.values || Date.now() - d.at > MAX_AGE_MS) { if (d) ls.del(s.key); return; }
  const now = snapshot(ov);
  const differs = Object.keys(d.values).some(k => k in now && JSON.stringify(now[k]) !== JSON.stringify(d.values[k]) && d.values[k] !== '' && d.values[k] !== false);
  if (!differs) return;
  const host = ov.querySelector('.modal-body') || ov.querySelector('.modal') || ov.firstElementChild;
  if (!host) return;
  const when = new Date(d.at);
  const label = (when.toDateString() === new Date().toDateString() ? 'today at ' : when.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' at ')
    + when.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const bar = document.createElement('div');
  bar.setAttribute('data-no-draft', '');
  bar.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;align-items:center;justify-content:space-between;background:#fff8e1;border:1px solid #f0d58a;color:#5c4400;border-radius:8px;padding:10px 12px;margin:0 0 14px;font-size:14px';
  bar.innerHTML = '<span>You have unsaved work in this window from ' + label + '.</span><span style="display:flex;gap:8px">'
    + '<button type="button" class="btn btn-primary btn-sm" data-act="restore">Restore it</button>'
    + '<button type="button" class="btn btn-secondary btn-sm" data-act="drop">Start fresh</button></span>';
  bar.addEventListener('click', (e) => {
    const act = e.target.closest('button') && e.target.closest('button').getAttribute('data-act');
    if (!act) return;
    e.preventDefault(); e.stopPropagation();
    if (act === 'restore') { restore(ov, d.values); s.dirty = true; toast.success('Your unsaved work is back.'); }
    else { ls.del(s.key); }
    bar.remove();
  });
  host.insertBefore(bar, host.firstChild);
}

function isCloseButton(btn, ov) {
  if (!btn || btn.closest('[data-no-draft]')) return false;
  const txt = (btn.textContent || '').trim();
  const aria = (btn.getAttribute('aria-label') || '').toLowerCase();
  return btn.classList.contains('modal-close') || aria === 'close'
    || ['✕', '×', '✖', 'Cancel', 'Close', 'Discard'].includes(txt)
    || (!!btn.closest('.modal-header') && txt.length <= 2);
}

// ── Listeners (capture phase, so they run before the pop-up's own handlers) ──
document.addEventListener('input', (e) => {
  const ov = overlayOf(e.target); if (!ov || !e.isTrusted || skip(e.target)) return;
  st(ov).dirty = true; saveSoon(ov); addMinimizeButton(ov);
}, true);
document.addEventListener('change', (e) => {
  const ov = overlayOf(e.target); if (!ov || !e.isTrusted || skip(e.target)) return;
  st(ov).dirty = true; saveSoon(ov);
}, true);

document.addEventListener('click', (e) => {
  const ov = overlayOf(e.target); if (!ov) return;
  const s = st(ov);
  // 1) click on the dark area around the window
  if (e.target === ov) {
    if (s.dirty) {
      e.stopPropagation(); e.preventDefault();
      toast('This window has unsaved changes. Use Save, or Cancel to close it.', { id: 'unsaved-hint', icon: '✋' });
    }
    return;
  }
  const btn = e.target.closest('button, a');
  if (!btn || !ov.contains(btn)) return;
  // 2) Cancel / ✕ / Close
  if (isCloseButton(btn, ov)) {
    if (s.dirty && !window.confirm('Discard your changes in this window?\n\nOK = discard   ·   Cancel = keep editing')) {
      e.stopPropagation(); e.preventDefault(); return;
    }
    if (s.key) ls.del(s.key);
    s.dirty = false;
    return;
  }
  // 3) Save / Create / Send: if the window closes soon after, the work was saved
  if (btn.tagName === 'BUTTON' && (btn.type === 'submit' || btn.closest('.modal-footer'))) {
    s.submitting = Date.now();
  }
}, true);

window.addEventListener('beforeunload', (e) => {
  const ov = topOverlay();
  if (ov && st(ov).dirty) { e.preventDefault(); e.returnValue = ''; }
});

// ── Shrink a pop-up and continue later ─────────────────────────────────────  [marker: confirm-polish v1]
let tray = null;
function trayEl() {
  if (tray && document.body.contains(tray)) return tray;
  tray = document.createElement('div');
  tray.setAttribute('data-no-draft', '');
  tray.style.cssText = 'position:fixed;left:50%;transform:translateX(-50%);bottom:calc(14px + env(safe-area-inset-bottom, 0px));z-index:1900;display:flex;gap:8px;flex-wrap:wrap;justify-content:center;max-width:calc(100vw - 32px)';
  document.body.appendChild(tray);
  return tray;
}
function chipFor(ov) {
  const s = st(ov);
  const chip = document.createElement('div');
  chip.style.cssText = 'display:flex;align-items:center;gap:8px;background:#1a3a5c;color:#fff;border-radius:999px;padding:6px 8px 6px 14px;box-shadow:0 6px 18px rgba(0,0,0,.25);font-size:13px;max-width:420px';
  const label = document.createElement('span');
  label.style.cssText = 'overflow:hidden;text-overflow:ellipsis;white-space:nowrap';
  label.textContent = '✎ ' + (titleOf(ov) || 'Unfinished window');
  const go = document.createElement('button');
  go.type = 'button';
  go.style.cssText = 'background:#fff;color:#1a3a5c;border:0;border-radius:999px;padding:4px 12px;font-weight:600;font-size:12px;cursor:pointer;white-space:nowrap';
  go.textContent = 'Continue';
  go.addEventListener('click', () => {
    if (document.contains(ov)) { ov.style.display = s.prevDisplay || ''; s.minimized = false; chip.remove(); return; }
    // the page changed and the window closed: go back to it; the draft is offered when it is reopened
    chip.remove();
    if (s.path && s.path !== window.location.pathname) { window.history.pushState({}, '', s.path); window.dispatchEvent(new PopStateEvent('popstate')); }
    toast('Open "' + (titleOf(ov) || 'the window') + '" again and choose "Restore it" to carry on.', { icon: '✎', duration: 7000 });
  });
  chip.appendChild(label); chip.appendChild(go);
  s.chip = chip;
  return chip;
}
function minimize(ov) {
  const s = st(ov);
  if (!s.key) s.key = keyOf(ov);
  if (s.key) ls.set(s.key, { at: Date.now(), values: snapshot(ov) }); // parking a window always keeps a draft
  s.prevDisplay = ov.style.display; s.path = window.location.pathname; s.minimized = true;
  ov.style.display = 'none';
  trayEl().appendChild(chipFor(ov));
}
function addMinimizeButton(ov) {
  if (ov.querySelector('[data-minimize]') || !fields(ov).length) return;
  const header = ov.querySelector('.modal-header');
  if (!header) return;
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'btn btn-ghost btn-icon';
  btn.setAttribute('data-minimize', ''); btn.setAttribute('data-no-draft', '');
  btn.setAttribute('aria-label', 'Shrink this window and continue later');
  btn.title = 'Shrink this window and continue later';
  btn.textContent = '—';
  btn.style.marginLeft = 'auto';
  btn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); minimize(ov); });
  const close = Array.from(header.querySelectorAll('button')).find(b => isCloseButton(b, ov));
  if (close) { close.parentNode.insertBefore(btn, close); close.style.marginLeft = close.style.marginLeft || '4px'; }
  else header.appendChild(btn);
}

// Watch pop-ups appear and disappear
const known = new Set();
new MutationObserver(() => {
  document.querySelectorAll(OVERLAY).forEach(ov => {
    if (!known.has(ov)) { known.add(ov); setTimeout(() => { if (document.contains(ov)) { offerDraft(ov); addMinimizeButton(ov); } }, 120); }
  });
  known.forEach(ov => {
    if (document.contains(ov)) return;
    known.delete(ov);
    const s = state.get(ov);
    if (s && s.key && s.submitting && Date.now() - s.submitting < 15000) ls.del(s.key); // saved
    if (s && s.chip && s.minimized && s.chip.firstChild) { // closed while shrunk (the page changed): offer the way back
      s.chip.firstChild.textContent = '✎ Unsaved: ' + (titleOf(ov) || 'window');
      if (s.chip.lastChild) s.chip.lastChild.textContent = 'Go back';
    }
  });
}).observe(document.documentElement, { childList: true, subtree: true });

// Clear out drafts older than two weeks
try {
  Object.keys(localStorage).filter(k => k.startsWith(PREFIX)).forEach(k => { const d = ls.get(k); if (!d || Date.now() - d.at > MAX_AGE_MS) ls.del(k); });
} catch (_) { /* ignore */ }
